import {
  FormContextProvider,
  FormContext,
  FormSchemaFields,
} from '@sw-sw/lib-form';
import { string, number, shape, arrayOf, array, func } from 'prop-types';
import React, { useContext, useEffect, useState } from 'react';
import iContextProviderProps from '../../../contexts/ClientContext';
import TenantFeatureContext from '../../../contexts/TenantContext';
import { env } from '../../../config';
import { useStates } from '../../../hooks/address';
import { useCountries } from '../../../hooks/address';
import validator from '../../../utils/FormValidator';
import { UIControlType } from '@sw-sw/lib-form-control-types';
import { defaultFindingObservationTemplates } from '../../../utils/clients';
import Loading from '../../Shared/ResourceIndex/Loading';
import clientApi from '../../../utils/api/client';
import ConfirmationModal from '../../Shared/ConfirmationModal/ConfirmationModal';
import { Modal, TooltipContextProvider } from '@sw-sw/lib-ui';
import { submitHandler } from '../../Shared/form';
import LockIcon from '@mui/icons-material/Lock';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import { IconButton } from '@mui/material';
import ToggleSmall from '../../Shared/Toggle/ToggleSmall';
import { ActionButtonsWithScrollFix } from '../../Shared/ActionButtons';
import ReactTooltip from 'react-tooltip';
import { toast } from 'react-toastify';
import userApi from '../../../utils/api/user';
import tenantApi from '../../../utils/api/tenant';
import ClientDocumentGroups from '../DocumentGroups/ClientDocumentGroups';

const clientShape = shape({
  name: string,
  id: number,
  address: shape({
    phone: string,
    street_1: string,
    street_2: string,
    city: string,
    zip: string,
    state: shape({ id: number }),
  }),
});

const deleteCustomDocType = async (
  docTypeId,
  setDeleteDocTypeError,
  setShowDeleteModal,
  docTypesRefetch,
) => {
  try {
    let clientId = window.location.pathname.split('/')[4];

    const deleteResponse = await clientApi.deleteCustomClientDocType.index(
      clientId,
      docTypeId,
    );

    if (deleteResponse.hasOwnProperty('Error')) {
      setDeleteDocTypeError(deleteResponse.Error);
    } else {
      setShowDeleteModal(false);
      docTypesRefetch.refetch();
    }
  } catch (error) {
    console.log(error);
  }
};

const editCustomDocType = async (
  docTypeId,
  setDeleteDocTypeError,
  setShowEditModal,
  docTypesRefetch,
  selectedDocTypeName,
) => {
  try {
    let clientId = window.location.pathname.split('/')[4];

    await clientApi.editCustomClientDocType.index(
      clientId,
      docTypeId,
      selectedDocTypeName,
    );
    docTypesRefetch.refetch();
  } catch (error) {
    console.log(error);
  }
};


function getSchemaClientInformation(states) {
  return {
    name: {
      label: 'Client Name',
      autoComplete: 'organization',
      validation: {
        required: true,
      },
      style: {
        flexBasis: '50%',
      },
    },
    phone: {
      label: 'Phone',
      autoComplete: 'tel',
      mask: env.REACT_APP_REGION === 'UK' ? 'phoneUK' : 'phone',
      validation: {
        required: env.REACT_APP_REGION === 'UK' ? false : true,
        format: env.REACT_APP_REGION === 'UK' ? 'phoneUK' : 'phone',
      },
      style: {
        flexBasis: '50%',
      },
    },
    street1: {
      label: 'Street 1',
      placeholder: 'Street',
      autoComplete: 'address-line1',
      'aria-label': 'Street address line 1 of 2',
      validation: {
        required: true,
      },
      style: {
        flexBasis: '50%',
      },
    },
    street2: {
      label: 'Street 2',
      placeholder: 'Street Line 2',
      'aria-label': 'Street address line 2 of 2',
      autoComplete: 'address-line2',
      style: {
        flexBasis: '50%',
      },
    },
    city: {
      placeholder: env.REACT_APP_REGION === 'UK' ? 'Town/City' : 'City',
      'aria-label': 'City',
      autoComplete: 'address-level2',
      validation: {
        required: true,
      },
      style: {
        flexBasis: '50%',
      },
    },
    state: {
      controlType: UIControlType.select,
      placeholder: env.REACT_APP_REGION === 'UK' ? 'Country' : 'State',
      'aria-label': 'State',
      autoComplete: 'address-level1',
      options: states,
      labelKey: 'name',
      valueKey: 'id',
      style: { flex: '1 1 25%' },
      validation: {
        required: true,
      },
    },
    zip: {
      placeholder: env.REACT_APP_REGION === 'UK' ? 'Postcode' : 'Zip',
      'aria-label': 'zip code',
      className: 'zip',
      autoComplete: 'postal-code',
      maxLength: 5,
      style: { flex: '1 1 25%' },
      validation: {
        required: true,
      },
      inputMode: 'numeric',
      parse: validator.parseNumber,
    },
    report_type: {
      label: 'Report Type',
      controlType: UIControlType.select,
      options: [
        { value: 'entire_inspection', label: 'Entire Inspection' },
        {
          value: 'entire_inspection_wlocations',
          label: 'Entire Inspection W/Finding Location',
        },
        {
          value: 'entire_inspection_wlocations_wphotos',
          label: 'Entire Inspection W/Finding Location and Photos',
        },
        { value: 'ca_log_wophotos', label: 'Findings Log - Without Photos' },
        { value: 'ca_log_wphotos', label: 'Findings Log - With Photos' },
        {
          value: 'findings_wphotos',
          label: 'Open Findings Report - With Photos',
        },
      ],
      style: {
        flexBasis: '50%',
        maxWidth: '50%',
      },
      labelKey: 'label',
      valueKey: 'value',
      toolTipOptions: {
        message:
          'Select a report type that will be automatically sent out when an inspection report is completed',
      },
      required: true,
    },
    code: {
      label: 'Code',
      autoComplete: 'code',
      style: {
        flexBasis: '50%',
        maxWidth: '50%',
      },
    },
    lock_report_type: {
      label: 'Lock Report Type',
      controlType: UIControlType.checkbox,
      className: 'responsive-form-checkbox',
      toolTipOptions: {
        message: 'When enabled, this report type will be used in all projects.',
      },
    },
  };
}

function getSchemaFindings(
  featureEnableEnhancedFinding,
  enablePhotoUploads,
  enableDissolvingPhotos,
  enableGuestSignOffs,
) {
  const oneThirdWidthStyle = { flex: '1 1 50%', alignSelf: 'flex-end' };

  ReactTooltip.rebuild();

  return {
    default_finding_observation: {
      label: 'Default Finding Observation',
      controlType: UIControlType.textareaTmpl,
      templates: defaultFindingObservationTemplates,
    },
    disable_images: {
      label: 'Disable Images',
      controlType: UIControlType.checkbox,
      style: {
        display: enablePhotoUploads ? 'block' : 'none',
        ...oneThirdWidthStyle,
        justifyItems: 'flex-start',
      },
    },
    disable_carry_overs: {
      label: 'Disable Carry Overs',
      controlType: UIControlType.checkbox,
      style: { justifyItems: 'flex-start', ...oneThirdWidthStyle },
    },
    finding_metric_tracking: {
      controlType: UIControlType.checkbox,
      label: 'Enable Enhanced Finding',
      toolTipOptions: {
        message:
          'When enabled, findings may be tied to specific legend items available for use within site map editor. This data will then be used for reporting.',
      },
      style: {
        justifyItems: 'flex-start',
        ...oneThirdWidthStyle,
        display: featureEnableEnhancedFinding ? 'block' : 'none',
      },
    },
    guest_sign_offs: {
      label: 'Guest Sign-Offs',
      controlType: UIControlType.checkbox,
      style: {
        justifyItems: 'flex-start',
        ...oneThirdWidthStyle,
        display: enableGuestSignOffs ? 'block' : 'none',
      },
    },
    dissolve_finding_images: {
      controlType: UIControlType.checkbox,
      label: 'Dissolving Photos',
      toolTipOptions: {
        message:
          '"When enabled, finding photos will be permanently deleted from the system once a date completed has been entered/the finding has been closed” These photos will not be able to be retrieved once they are deleted.',
      },
      style: {
        justifyItems: 'flex-start',
        ...oneThirdWidthStyle,
        display:
          enableDissolvingPhotos && enablePhotoUploads ? 'block' : 'none',
      },
    },
    show_observation_dates_of_findings: {
      label: 'Show Observation Dates in Findings',
      controlType: UIControlType.checkbox,
      style: {
        justifyItems: 'flex-start',
        ...oneThirdWidthStyle,
      },
    }
  };
}

function getSchemaPublicViewTabs(publicViewTabs, publicViewEnabled) {
  return {
    publicViewTabs: {
      label: 'Available Tabs',
      controlType: UIControlType.checkboxGroup,
      options: publicViewTabs,
      valueKey: 'id',
      labelKey: 'name',
      disabled: !publicViewEnabled,
      style: { flexBasis: '50%' },
    },
  };
}

function getSchemaPublicView() {
  return {
    public_view_enabled: {
      controlType: UIControlType.checkbox,
      label: 'Enable Public View',
      toolTipOptions: {
        message:
          'When enabled, users with the QR Access permission can generate a public view page and QR code for projects under this client.',
      },
      style: { justifyItems: 'flex-start' },
    },
  };
}

function getSchemaVisitorGate(publicViewEnabled) {
  return {
    visitor_gate_enabled: {
      controlType: UIControlType.checkbox,
      label: 'Enable Visitor Gate Form',
      disabled: !publicViewEnabled,
      toolTipOptions: {
        message:
          'When enabled, visitors who access the public view will be prompted to enter their name, email, and company.',
      },
      style: { justifyItems: 'flex-start' },
    },
    visitor_gate_required: {
      controlType: UIControlType.checkbox,
      label: 'Require Visitor Gate Form',
      disabled: !publicViewEnabled,
      toolTipOptions: {
        message:
          'When enabled, visitors must complete the gate form before accessing the public view. When disabled, visitors may skip the form.',
      },
      style: { justifyItems: 'flex-start' },
    },
  };
}

function getSchemaProjectTabs(projectTabs) {
  return {
    projectTabs: {
      label: 'Project Tabs',
      controlType: UIControlType.checkboxGroup,
      options: projectTabs,
      valueKey: 'id',
      labelKey: 'name',
      style: { flexBasis: '50%' },
    },
  };
}

function getSchemaDocumentDate(document_date) {
  return {
   document_date: {
      label: 'Document Date',
      controlType: UIControlType.checkboxGroup,
      options: [document_date],
      valueKey: 'id',
      labelKey: 'name',
    },
  };
}


function getSchemeCertificationBackdated(
  enableMetadataReuirement,
  enableForecastWeather,
  enableInspectionScheduler,
  forecastWeatherEnabled,
) {
  ReactTooltip.rebuild();

  return {
    backdated: {
      label: 'Disable Missed Inspection Backdating',
      controlType: UIControlType.checkbox,
      className: 'responsive-form-checkbox',
      toolTipOptions: {
        message:
          'When this feature is checked, inspections submitted after the date of inspection will be considered as missed, even if the inspection certification is backdated to the date of inspection. When this feature is not checked inspections submitted after the date of inspection will not be considered as missed if the inspection certification is backdated to the date of inspection.',
      },
    },
    forecast_weather: {
      controlType: UIControlType.checkbox,
      label: 'NOAA Weather Table',
      style: {
        display: enableForecastWeather ? 'block' : 'none',
      },
    },
    enable_photo_metadata_requirement: {
      controlType: UIControlType.checkbox,
      label: 'Enable Photo Metadata Requirement',
      style: {
        display: enableMetadataReuirement ? 'block' : 'none',
      },
    },
    ca_inspection_scheduler: {
      controlType: UIControlType.checkbox,
      label: 'CA - Inspection Scheduler',
      style: {
        display:
          enableInspectionScheduler && forecastWeatherEnabled
            ? 'block'
            : 'none',
      },
      disabled: !forecastWeatherEnabled,
    },
  };
}

function getSchemaInspectionCertification(
  isEnabled,
  isSameForBoth,
  selectedcertificationType,
) {
  ReactTooltip.rebuild();
  const schema = {
    certification_type: {
      label: 'Certification Type',
      controlType: UIControlType.select,
      labelKey: 'label',
      valueKey: 'value',
      options: [
        { value: 'compliance', label: 'Compliance' },
        { value: 'acknowledgement', label: 'Acknowledgement' },
        { value: 'both', label: 'Both' },
      ],
      validation: {
        required: true,
      },
      style: {
        flexBasis: '33%',
      },
    },
    no_of_days_compliance: {
      label: 'Due In (Compliance)',
      controlType: UIControlType.number,
      toolTipOptions: {
        message:
          'Set the number of days after an inspection is submitted by which this certification must be signed',
      },
      style: {
        flexBasis: '33%',
      },
      validation: {
        required: false,
      },
      disabled: true,
    },
    no_of_days_acknowledgment: {
      label: 'Due In (Acknowledgement)',
      controlType: UIControlType.number,
      toolTipOptions: {
        message:
          'Set the number of days after an inspection is submitted by which this certification must be signed',
      },
      style: {
        flexBasis: '33%',
      },
      validation: {
        required: false,
      },
      disabled: true,
    },
    sameForBoth: {
      label: 'Same for both',
      controlType: UIControlType.checkbox,
      style: {
        flexBasis: '100%',
        alignSelf: 'center',
        display: 'none',
      },
    },
  };

  if (['compliance', 'both'].includes(selectedcertificationType)) {
    if (schema.no_of_days_compliance.validation) {
      schema.no_of_days_compliance.validation.required = true;
    }
    schema.no_of_days_compliance.disabled = false;
  }

  if (['acknowledgement', 'both'].includes(selectedcertificationType)) {
    if (schema.no_of_days_acknowledgment.validation) {
      schema.no_of_days_acknowledgment.validation.required = true;
    }
    schema.no_of_days_acknowledgment.disabled = false;
  }

  if (isEnabled && selectedcertificationType === 'both') {
    schema.sameForBoth.style.display = 'block';
  }

  if (isSameForBoth && selectedcertificationType === 'both') {
    schema.no_of_days_compliance.label = 'Due In';
    if (schema.no_of_days_compliance.toolTipOptions) {
      schema.no_of_days_compliance.toolTipOptions.message =
        'Set the number of days after an inspection is submitted by which both the certifications must be signed';
    }
    delete schema.no_of_days_acknowledgment;
  }

  if (!isEnabled) {
    delete schema.certification_type;
    delete schema.no_of_days_compliance;
    delete schema.no_of_days_acknowledgment;
  }

  return schema;
}

function getSchemePrecipitation(tenantPrecipitationTracking) {
  return {
    precipitation_client_project_flag: {
      controlType: UIControlType.toggleButton,
      toggleSize: 'sm',
      style: {
        marginTop: '10px',
        marginLeft: '40px',
      },
      disabled: !tenantPrecipitationTracking ? true : false,
    },
  };
}

function getSchemaWeatherStation(isTempestSelected, handleOnAddToken) {
  ReactTooltip.rebuild();
  const schema = {
    weather_station_type: {
      label: 'Weather Station',
      controlType: UIControlType.select,
      options: [
        { value: 'tomorrow.io', label: 'Tomorrow.io' },
        { value: 'tempest', label: 'Tempest' },
      ],
      style: {
        marginBottom: isTempestSelected ? '0rem' : '1rem',
      },
    },
    tempest_access_token: {
      label: 'Tempest Access Token',
      controlType: UIControlType.custom,
      renderControl: ({ onChange, value, ...props }) => {
        return (
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <input
              type='text'
              value={
                value
                  ? value
                      .split('') // turn into array of characters
                      .map((ch, i) => {
                        // keep dashes
                        if (ch === '-') return '-';

                        // show last 4 non-dash chars only
                        const nonDashChars = value.replace(/-/g, '');
                        const visibleStart = nonDashChars.length - 4;
                        const nonDashIndex =
                          value.slice(0, i + 1).replace(/-/g, '').length - 1;

                        return nonDashIndex >= visibleStart ? ch : '*';
                      })
                      .join('')
                  : ''
              }
              onChange={(e) => {
                onChange(e.target.value);
              }}
              {...props}
              style={{ flex: 1, marginRight: '0.5rem' }}
              disabled
            />
            <button
              type='button'
              className=''
              style={{
                height: '46px',
              }}
              onClick={handleOnAddToken}
            >
              {value ? 'Update Token' : 'Add Token'}
            </button>
          </div>
        );
      },
    },
    is_tempest_locked: {
      label: 'Lock Weather Station',
      controlType: UIControlType.checkbox,
      className: 'responsive-form-checkbox',
      toolTipOptions: {
        message:
          'When enabled, tempest weather station will be used in all projects of this Client.',
      },
      style: {
        marginTop: '0.5rem',
      },
    },
  };

  if (!isTempestSelected) {
    delete schema.tempest_access_token;
    delete schema.is_tempest_locked;
  }

  return schema;
}

function getSchemaPDFEditing(isEnabled) {
  ReactTooltip.rebuild();
  const schema = {
    append_only_enabled: {
      label: 'Enable PDF Annotation Restriction',
      controlType: UIControlType.checkbox,
      className: 'responsive-form-checkbox',
      toolTipOptions: {
        message:
          'When enabled, PDF editing is limited to the active user session, ensuring that all previously added updates remain locked and cannot be altered or removed.',
      },
    },
    lock_append_only: {
      label: 'Lock PDF Annotation Restriction',
      controlType: UIControlType.checkbox,
      className: 'responsive-form-checkbox',
      toolTipOptions: {
        message:
          'When enabled, the PDF annotation restriction setting will be locked for all projects under this Client, preventing any changes to this configuration.',
      },
      style: {
        display: isEnabled ? 'block' : 'none',
      },
    },
  };

  if (!isEnabled) {
    delete schema.lock_append_only;
  }
  return schema;
}

function getInitialData(client) {
  const { address = {} } = client;

  return {
    name: client.name,
    code: client.code,
    street1: address.street_1,
    street2: address.street_2,
    city: address.city,
    state:
      env.REACT_APP_REGION === 'UK'
        ? address.countryObj && address.countryObj.id
        : address.state && address.state.id,
    zip: address.zip,
    phone: address.phone,
    default_finding_observation: client.default_finding_observation,
    disable_images: client.disable_images,
    precipitation_client_project_flag: client.precipitation_client_project_flag,
    disable_carry_overs: client.disable_carry_overs,
    finding_metric_tracking: client.finding_metric_tracking,
    dissolve_finding_images: client.dissolve_finding_images,
    enable_photo_metadata_requirement: client.enable_photo_metadata_requirement,
    forecast_weather: client.forecast_weather,
    ca_inspection_scheduler: client.ca_inspection_scheduler || false,
    backdated: client.backdated_certification,
    report_type: client.report_type,
    lock_report_type: client.lock_report_type,
    guest_sign_offs: client.guest_sign_offs,
    is_inspection_certification_timeline_enabled:
      client.is_inspection_certification_timeline_enabled,
    is_inspection_certification_timeline_locked:
      client.is_inspection_certification_timeline_locked,
    certification_type: client.certification_type || 'compliance',
    no_of_days_compliance: client.no_of_days_compliance || 0,
    no_of_days_acknowledgment: client.no_of_days_acknowledgment || 0,
    sameForBoth:
      client.no_of_days_acknowledgment === client.no_of_days_compliance,
    weather_station_type: client.weather_station_type || 'tomorrow.io',
    tempest_access_token: client.tempest_access_token || '',
    is_tempest_locked: client.is_tempest_locked || false,
    append_only_enabled: client.append_only_enabled || false,
    lock_append_only: client.lock_append_only || false,
    show_observation_dates_of_findings: client.show_observation_dates_of_findings || false,
    public_view_enabled: client.public_view_enabled || false,
    visitor_gate_enabled: client.visitor_gate_enabled || false,
    visitor_gate_required: client.visitor_gate_required !== undefined ? client.visitor_gate_required : true,
  };
}

function getInitialPublicViewTabData(publicViewTabs) {
  const data = {};
  if (publicViewTabs) {
    publicViewTabs.forEach((tab) => {
      data[tab.id] = tab.isChecked;
    });
  }
  return data;
}

function getHeaders(header, width) {
  return (
    <div style={{ width: width }} className='client-settings-list-item'>
      <div>{header}</div>
    </div>
  );
}

function CustomList({ data }) {
  const [listData, setListData] = useState(data);

  const handleChange = (key, value, id) => {
    const updatedList = listData.map((item) => {
      if (item.id === id) {
        clientApi.projects.updateProjectPrecipitationDetails(id, {
          ...item,
          [key]: value,
        });

        return { ...item, [key]: value };
      }

      return item;
    });

    setListData(updatedList);
  };

  if (!listData.length) {
    return <>There is no data to track</>;
  }

  return (
    <div className='client-settings-list-container'>
      <div className='client-settings-list-header'>
        <div
          style={{ width: '15%', justifyContent: 'flex-start' }}
          className='client-settings-list-item'
        >
          <div>Project Name</div>
        </div>
        {getHeaders('', '10%')}
        {getHeaders('Rain Threshold (inches)', '30%')}
        {getHeaders('Snow Threshold (inches)', '30%')}
        {getHeaders('Feature Lock', '15%')}
      </div>
      <div className='client-settings-list-body'>
        {listData.map((project) => (
          <div key={project.id} className='client-settings-list-row'>
            <div
              style={{ width: '15%', justifyContent: 'flex-start' }}
              className='client-settings-list-item'
            >
              <div>{project.name}</div>
            </div>

            {getHeaders(
              <ToggleSmall
                isChecked={project.precipitation_flag}
                handleChange={(e) => {
                  handleChange(
                    'precipitation_flag',
                    e.target.checked,
                    project.id,
                  );
                }}
                disabled={project.precipitation_edit_lock}
              />,
              '10%',
            )}
            {getHeaders(
              <input
                type='number'
                value={project.precipitation_threshold ?? ''}
                onChange={(e) => {
                  handleChange(
                    'precipitation_threshold',
                    Number(e.target.value || 0),
                    project.id,
                  );
                }}
                disabled={project.precipitation_edit_lock}
                style={{
                  border: '1px solid #e8e4df',
                  padding: '0.5rem 0.75rem',
                  color: '#0d2330',
                }}
              />,
              '30%',
            )}
            {getHeaders(
              <input
                type='number'
                value={project.snow_threshold ?? ''}
                onChange={(e) => {
                  handleChange(
                    'snow_threshold',
                    Number(e.target.value || 0),
                    project.id,
                  );
                }}
                disabled={project.precipitation_edit_lock}
                style={{
                  border: '1px solid #e8e4df',
                  padding: '0.5rem 0.75rem',
                  color: '#0d2330',
                }}
              />,
              '30%',
            )}
            {getHeaders(
              <IconButton
                size='small'
                onClick={() => {
                  handleChange(
                    'precipitation_edit_lock',
                    !project.precipitation_edit_lock,
                    project.id,
                  );
                }}
              >
                {project.precipitation_edit_lock ? (
                  <LockIcon className='col-danger' opacity={1} />
                ) : (
                  <LockOpenIcon className='col-success' opacity={1} />
                )}
              </IconButton>,
              '15%',
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function ClientSettingsTabUI({
  states,
  docTypes,
  projectTabs,
  publicViewTabs,
  document_date,
  docTypesRefetch,
  toSubmitOrderData,
  setToSubmitOrderData,
  publicViewCollapseRef,
  ...props
}) {
  const formContext = useContext(FormContext);
  const context = useContext(iContextProviderProps);
  const [allClientProjects] = useState(context.clientProjects);
  const { tenantHasFeature } = useContext(TenantFeatureContext);

  const [clientInformationCollapse, setClientInformationCollapse] =
    useState(true);
  const [projectTabsCollapse, setProjectTabsCollapse] = useState(true);
  const [findingsCollapse, setFindingsCollapse] = useState(true);
  const [inspectionCollapse, setInspectionCollapse] = useState(true);
  const [projectDocCollapse, setProjectDocCollapse] = useState(true);
  const [pdfEditingCollapse, setPdfEditingCollapse] = useState(true);
  const [publicViewCollapse, _setPublicViewCollapse] = useState(
    publicViewCollapseRef ? publicViewCollapseRef.current : true,
  );
  const setPublicViewCollapse = (val) => {
    _setPublicViewCollapse(val);
    if (publicViewCollapseRef) publicViewCollapseRef.current = val;
  };
  const [precipitationTrackingCollapse, setPrecipitationTrackingCollapse] =
    useState(true);

  const [showInspectionCertTimeline, setShowInspectionCertTimeline] =
    useState(true);
  const [pdfEditingStatus, setPdfEditingStatus] = useState('disabled');

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showAddTokenModal, setShowAddTokenModal] = useState(false);

  const [accessTokenInput, setAccessTokenInput] = useState('');

  const [selectedDocTypeId, setSelectedDocTypeId] = useState(false);
  const [deleteDocTypeError, setDeleteDocTypeError] = useState('');
  const [selectedDocTypeName, setSelectedDocTypeName] = useState('');
  const [tempReorderDocTypes, setTempReorderDocTypes] = useState([]);

  let clientId = window.location.pathname.split('/')[4];

  // Sync publicViewTabs into form context once async data loads.
  // FormContextProvider initialises before the query resolves, so the initial
  // value is {}. This effect back-fills the real saved state so that a save
  // without touching the checkboxes still writes the correct full set of tabs.
  useEffect(() => {
    if (!publicViewTabs || publicViewTabs.length === 0) return;
    const currentForm = formContext.value.publicViewTabs;
    const hasAnyInteraction = currentForm && Object.keys(currentForm).length > 0;
    if (!hasAnyInteraction) {
      const synced = {};
      publicViewTabs.forEach((tab) => { synced[tab.id] = tab.isChecked; });
      formContext.set('publicViewTabs', synced);
    }
  }, [publicViewTabs]);

  useEffect(() => {
    let filteredDT =
      // these two doc types exists in db, but needs to be displayed in very few places, in the frontend. So, remove manually where not required.
      docTypes &&
      docTypes.filter(
        (e) => e.name !== 'Fixed Details' && e.name !== 'Fixed Details Project',
      );

    setTempReorderDocTypes([
      ...(filteredDT || ''),
      ...(props.newDocTypes || ''),
    ]);
  }, [docTypes, props.newDocTypes]);

  useEffect(() => {
    let mounted = true;
    userApi.tenantInfoInspection().then((data) => {
      if (!mounted) return;
      setShowInspectionCertTimeline(
        data?.getInspectionCertTimeline
          ?.is_inspection_certification_timeline_enabled &&
          !data?.getInspectionCertTimeline?.is_locked,
      );
    });
    tenantApi.pdfEditting
      .getAppendOnlyEnabled(props.client.tenant_id)
      .then((res) => {
        if (!mounted) return;
        if (res) {
          if (!res.append_only_enabled) {
            setPdfEditingStatus('disabled');
          } else if (res.lock_append_only) {
            setPdfEditingStatus('locked');
          } else {
            setPdfEditingStatus('enabled');
          }
        }
      });
    return () => { mounted = false; };
  }, []);

  // Auto-disable inspection scheduler when forecast weather is disabled
  useEffect(() => {
    if (
      !formContext.value.forecast_weather &&
      formContext.value.ca_inspection_scheduler
    ) {
      formContext.set('ca_inspection_scheduler', false);
    }
  }, [formContext.value.forecast_weather]);


  return (
    <div className='client-settings-tab'>
      <div className='client-information-section'>
        <div className='client-settings-tab-save'>
          <section>
            <label>Update Client</label>
          </section>
          <ActionButtonsWithScrollFix
            primary='Save'
            spacer={false}
            onClick={(e) => {
              if (formContext.value.public_view_enabled) {
                const tabs = formContext.value.publicViewTabs || {};
                const anyChecked = Object.values(tabs).some(Boolean);
                if (!anyChecked) {
                  toast.error('At least one Available Tab must be selected when Public View is enabled.');
                  // Restore publicViewTabs to the last saved state
                  const restored = {};
                  props.publicViewTabs && props.publicViewTabs.forEach(tab => {
                    restored[tab.id] = tab.isChecked;
                  });
                  formContext.set('publicViewTabs', restored);
                  return;
                }
              }
              submitHandler(formContext, e, props.handleSubmit);
            }}
          />
        </div>
        <div
          className='client-information-section-header'
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            cursor: 'pointer',
          }}
          onClick={() =>
            setClientInformationCollapse(!clientInformationCollapse)
          }
        >
          <h3> Client Information </h3>
          <i
            className={
              clientInformationCollapse
                ? 'fa fa-chevron-right'
                : 'fa fa-chevron-down'
            }
            aria-hidden='true'
          />
        </div>
        <hr></hr>
        <br />
        <div
          className={
            clientInformationCollapse
              ? 'client-settings-section-hidden'
              : 'client-settings-section-visible'
          }
        >
          <FormSchemaFields
            schema={getSchemaClientInformation(states)}
            onChange={formContext.set}
            formData={formContext.value}
            initialFormData={getInitialData(props.client)}
          />
        </div>
      </div>
      <div className='project-tabs-section'>
        <div
          className='project-tabs-section-header'
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            cursor: 'pointer',
          }}
          onClick={() => setProjectTabsCollapse(!projectTabsCollapse)}
        >
          <h3> Project Tabs </h3>
          <i
            className={
              projectTabsCollapse
                ? 'fa fa-chevron-right'
                : 'fa fa-chevron-down'
            }
            aria-hidden='true'
          />
        </div>
        <hr></hr>
        <br />
        <div
          className={
            projectTabsCollapse
              ? 'client-settings-section-hidden'
              : 'client-settings-section-visible'
          }
        >
          <FormSchemaFields
            schema={getSchemaProjectTabs(projectTabs)}
            onChange={formContext.set}
            formData={formContext.value}
            initialFormData={getInitialData(
              props.client,
              docTypes,
              projectTabs,
            )}
          />
        </div>
      </div>
      <div className='inspections-section'>
        <div
          className='inspections-section-header'
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            cursor: 'pointer',
          }}
          onClick={() => setInspectionCollapse(!inspectionCollapse)}
        >
          <h3> Inspections </h3>
          <i
            className={
              inspectionCollapse ? 'fa fa-chevron-right' : 'fa fa-chevron-down'
            }
            aria-hidden='true'
          />
        </div>
        <hr></hr>
        <br />
        <div
          className={
            inspectionCollapse
              ? 'client-settings-section-hidden'
              : 'client-settings-section-visible'
          }
        >
          <FormSchemaFields
            schema={getSchemeCertificationBackdated(
              tenantHasFeature('Enable Photo Metadata Requirement'),
              tenantHasFeature('NOAA Weather Table'),
              tenantHasFeature('CA - Inspection Scheduler'),
              formContext.value.forecast_weather,
            )}
            onChange={formContext.set}
            formData={formContext.value}
            initialFormData={getInitialData(props.client)}
          />
          {showInspectionCertTimeline && (
            <>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  flexDirection: 'row',
                  gap: '10px',
                }}
              >
                <FormSchemaFields
                  schema={{
                    is_inspection_certification_timeline_enabled: {
                      label: 'Enable Inspection Certification Timeline',
                      controlType: UIControlType.checkbox,
                    },
                  }}
                  formData={formContext.value}
                  onChange={formContext.set}
                  initialFormData={getInitialData(props.client)}
                />
                {formContext.value
                  .is_inspection_certification_timeline_enabled && (
                  <IconButton
                    size='small'
                    onClick={() =>
                      formContext.set(
                        'is_inspection_certification_timeline_locked',
                        !formContext.value
                          .is_inspection_certification_timeline_locked,
                      )
                    }
                    data-tip='Lock across all Projects in this Client'
                    style={{
                      marginTop: '-25px',
                    }}
                  >
                    {formContext.value
                      .is_inspection_certification_timeline_locked ? (
                      <LockIcon className='col-danger' fontSize='small' />
                    ) : (
                      <LockOpenIcon className='col-success' fontSize='small' />
                    )}
                  </IconButton>
                )}
              </div>
              <FormSchemaFields
                schema={getSchemaInspectionCertification(
                  formContext.value
                    .is_inspection_certification_timeline_enabled,
                  formContext.value.sameForBoth,
                  formContext.value.certification_type || '',
                )}
                onChange={formContext.set}
                formData={formContext.value}
                initialFormData={getInitialData(props.client)}
                className='findings-form'
              />
            </>
          )}
        </div>
      </div>
      <div className='findings-section'>
        <div
          className='findings-section-header'
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            cursor: 'pointer',
          }}
          onClick={() => setFindingsCollapse(!findingsCollapse)}
        >
          <h3> Findings / Action Items </h3>
          <i
            className={
              findingsCollapse ? 'fa fa-chevron-right' : 'fa fa-chevron-down'
            }
            aria-hidden='true'
          />
        </div>
        <hr></hr>
        <br />
        <div
          className={
            findingsCollapse
              ? 'client-settings-section-hidden'
              : 'client-settings-section-visible'
          }
        >
          <FormSchemaFields
            schema={getSchemaFindings(
              tenantHasFeature('Enable Enhanced Findings'),
              tenantHasFeature('Enable Photo Uploads'),
              tenantHasFeature('Enable Dissolving Finding Photos'),
              tenantHasFeature('Guest Sign-Offs'),
            )}
            onChange={formContext.set}
            formData={formContext.value}
            initialFormData={getInitialData(props.client)}
          />
        </div>
      </div>
      <div className='project-doc-section'>
        <div
          className='project-doc-section-header'
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            cursor: 'pointer',
          }}
          onClick={() => setProjectDocCollapse(!projectDocCollapse)}
        >
          <h3> Project Document Folders </h3>{' '}
          <i
            className={
              projectDocCollapse ? 'fa fa-chevron-right' : 'fa fa-chevron-down'
            }
            aria-hidden='true'
          />
        </div>
        <hr></hr>
        <br />
        <div
          className={
            projectDocCollapse
              ? 'client-settings-section-hidden'
              : 'client-settings-section-visible'
          }
        >
          <ClientDocumentGroups clientId={parseInt(clientId)} />
          <FormSchemaFields
            schema={getSchemaDocumentDate(document_date)}
            onChange={formContext.set}
            formData={formContext.value}
            initialFormData={getInitialData(props.client)}
          />
          
        </div>
      </div>
      <div className='pdfediting-section'>
        <div
          className='pdfediting-section-header'
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            cursor: 'pointer',
          }}
          onClick={() => setPdfEditingCollapse(!pdfEditingCollapse)}
        >
          <h3> PDF Editing </h3>
          <i
            className={
              pdfEditingCollapse ? 'fa fa-chevron-right' : 'fa fa-chevron-down'
            }
            aria-hidden='true'
          />
        </div>
        <hr></hr>
        <br />
        <div
          className={
            pdfEditingCollapse
              ? 'client-settings-section-hidden'
              : 'client-settings-section-visible'
          }
        >
          {pdfEditingStatus === 'disabled' && (
            <section className='col-danger'>
              <p>This setting is disabled by Admin.”</p>
            </section>
          )}
          {pdfEditingStatus === 'locked' && (
            <section className='col-danger'>
              <p>This setting is locked by Admin.</p>
            </section>
          )}
          {pdfEditingStatus === 'enabled' && (
            <FormSchemaFields
              schema={getSchemaPDFEditing(
                formContext.value.append_only_enabled || false,
              )}
              onChange={formContext.set}
              formData={formContext.value}
              initialFormData={getInitialData(props.client)}
              className='findings-form'
            />
          )}
        </div>
      </div>
      {tenantHasFeature('Public View QR Code') && (
        <div className='public-view-section'>
          <div
            className='public-view-section-header'
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              cursor: 'pointer',
            }}
            onClick={() => setPublicViewCollapse(!publicViewCollapse)}
          >
            <h3>Public View</h3>
            <i
              className={
                publicViewCollapse ? 'fa fa-chevron-right' : 'fa fa-chevron-down'
              }
              aria-hidden='true'
            />
          </div>
          <hr />
          <br />
          <div
            className={
              publicViewCollapse
                ? 'client-settings-section-hidden'
                : 'client-settings-section-visible'
            }
          >
            <div style={{ display: 'flex', gap: '120px', alignItems: 'flex-start' }}>
              {/* Left column — feature flag controls */}
              <div style={{ flex: '0 0 auto', minWidth: '260px' }}>
                <h4 style={{ margin: '0 0 12px' }}>Public View Controls</h4>
                {[
                  {
                    field: 'public_view_enabled',
                    label: 'Enable Public View',
                    tip: 'When enabled, users with the QR Access permission can generate a public view page and QR code for projects under this client.',
                    disabled: false,
                  },
                  {
                    field: 'visitor_gate_enabled',
                    label: 'Enable Visitor Gate Form',
                    tip: 'When enabled, visitors who access the public view will be prompted to enter their name, email, and company.',
                    disabled: !formContext.value.public_view_enabled,
                  },
                  {
                    field: 'visitor_gate_required',
                    label: 'Require Visitor Gate Form',
                    tip: 'When enabled, visitors must complete the gate form before accessing the public view. When disabled, visitors may skip the form.',
                    disabled: !formContext.value.public_view_enabled || !formContext.value.visitor_gate_enabled,
                  },
                ].map(({ field, label, tip, disabled }) => (
                  <div
                    key={field}
                    style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '10px', opacity: disabled ? 0.5 : 1 }}
                  >
                    <input
                      type='checkbox'
                      id={`pv-${field}`}
                      checked={formContext.value[field] !== undefined ? formContext.value[field] : field === 'visitor_gate_required'}
                      disabled={disabled}
                      onChange={(e) => {
                        const checked = e.target.checked;
                        formContext.set(field, checked);
                        // Cascade: enabling public view auto-enables both gate fields
                        if (field === 'public_view_enabled' && checked) {
                          formContext.set('visitor_gate_enabled', true);
                          formContext.set('visitor_gate_required', true);
                        }
                        // Cascade: enabling visitor gate auto-enables require
                        if (field === 'visitor_gate_enabled' && checked) {
                          formContext.set('visitor_gate_required', true);
                        }
                      }}
                      style={{ margin: 0, flexShrink: 0, width: '16px', height: '16px', cursor: disabled ? 'default' : 'pointer' }}
                    />
                    <label htmlFor={`pv-${field}`} style={{ margin: 0, cursor: disabled ? 'default' : 'pointer', whiteSpace: 'nowrap' }}>
                      {label}
                    </label>
                    <span
                      data-tip={tip}
                      data-place='right'
                      style={{ cursor: 'help', color: '#888', fontSize: '13px', lineHeight: 1 }}
                    >
                      <i className='fa fa-question-circle' />
                    </span>
                  </div>
                ))}
              </div>

              {/* Right column — available tabs (filtered to only tabs enabled in Project Tabs) */}
              {publicViewTabs && publicViewTabs.length > 0 && (
                <div style={{ flex: 1 }}>
                  <h4 style={{ margin: '0 0 12px' }}>Available Tabs</h4>
                  {publicViewTabs
                    .filter((tab) => {
                      const projectTabs = formContext.value.projectTabs;
                      if (!projectTabs) return true;
                      return projectTabs[tab.id] !== false;
                    })
                    .map((tab) => {
                    const disabled = !formContext.value.public_view_enabled;
                    const checked = formContext.value.publicViewTabs
                      ? formContext.value.publicViewTabs[tab.id] !== false
                      : tab.isChecked;
                    return (
                      <div
                        key={tab.id}
                        style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '10px', opacity: disabled ? 0.5 : 1 }}
                      >
                        <input
                          type='checkbox'
                          id={`pvt-${tab.id}`}
                          checked={checked}
                          disabled={disabled}
                          onChange={(e) =>
                            formContext.set('publicViewTabs', {
                              ...formContext.value.publicViewTabs,
                              [tab.id]: e.target.checked,
                            })
                          }
                          style={{ margin: 0, flexShrink: 0, width: '16px', height: '16px', cursor: disabled ? 'default' : 'pointer' }}
                        />
                        <label htmlFor={`pvt-${tab.id}`} style={{ margin: 0, cursor: disabled ? 'default' : 'pointer', whiteSpace: 'nowrap' }}>
                          {tab.name}
                        </label>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
      <div className='precipitation-tracking-section'>
        <div
          className='precipitation-tracking-section-header'
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            cursor: 'pointer',
          }}
          onClick={() =>
            setPrecipitationTrackingCollapse(!precipitationTrackingCollapse)
          }
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h3>Precipitation Tracking</h3>
            <FormSchemaFields
              schema={getSchemePrecipitation(
                tenantHasFeature('Precipitation Tracking'),
              )}
              onChange={formContext.set}
              formData={formContext.value}
              initialFormData={getInitialData(props.client)}
            />
          </div>
          {tenantHasFeature('Precipitation Tracking') && (
            <i
              className={
                precipitationTrackingCollapse
                  ? 'fa fa-chevron-right'
                  : 'fa fa-chevron-down'
              }
              aria-hidden='true'
            />
          )}
        </div>
        {!tenantHasFeature('Precipitation Tracking') ? (
          <section className='col-danger' style={{ fontSize: '14px' }}>
            <p>
              “Precipitation Tracking is not included in this accounts license
              agreement.To turn on please contact your company's administrator.”
            </p>
          </section>
        ) : (
          <>
            <hr></hr>
            <div
              className={
                precipitationTrackingCollapse
                  ? 'client-settings-section-hidden'
                  : 'client-settings-section-visible'
              }
            >
              {formContext.value.precipitation_client_project_flag ? (
                <>
                  {tenantHasFeature('Enable Tempest') && (
                    <section className='client-precip-form'>
                      <FormSchemaFields
                        schema={getSchemaWeatherStation(
                          formContext.value.weather_station_type === 'tempest',
                          () => {
                            setShowAddTokenModal(true);
                            setAccessTokenInput(
                              formContext.value.tempest_access_token,
                            );
                          },
                        )}
                        onChange={formContext.set}
                        formData={formContext.value}
                      />
                    </section>
                  )}
                  <CustomList data={allClientProjects} />
                </>
              ) : (
                <>There is no data to track</>
              )}
            </div>
          </>
        )}
      </div>

      {showEditModal && (
        <Modal
          title='Edit Name'
          show={true}
          handleClose={() => {
            setShowEditModal(false);
          }}
          handleSubmit={() => {
            editCustomDocType(
              selectedDocTypeId,
              setDeleteDocTypeError,
              setShowDeleteModal,
              docTypesRefetch,
              selectedDocTypeName,
            );
            setShowEditModal(false);
          }}
          submitBtnText='Edit'
        >
          <input
            type='text'
            value={selectedDocTypeName}
            onChange={(event) => setSelectedDocTypeName(event.target.value)}
          />
        </Modal>
      )}

      {showDeleteModal && (
        <ConfirmationModal
          title='Delete Folder'
          subTitle={
            deleteDocTypeError
              ? `${deleteDocTypeError}`
              : `The selected Folder will be deleted and removed from all Projects associated with this client. Are you sure you want to proceed?`
          }
          show={true}
          handleClose={() => {
            setShowDeleteModal(false);
            setDeleteDocTypeError('');
          }}
          handleConfirm={() =>
            deleteCustomDocType(
              selectedDocTypeId,
              setDeleteDocTypeError,
              setShowDeleteModal,
              docTypesRefetch,
            )
          }
          buttonText='Delete'
        />
      )}

      {showAddTokenModal && (
        <Modal
          title={
            formContext.value.tempest_access_token ? 'Edit Token' : 'Add Token'
          }
          show={true}
          handleClose={() => {
            setShowAddTokenModal(false);
          }}
          handleSubmit={() => {
            formContext.set('tempest_access_token', accessTokenInput.trim());
            setShowAddTokenModal(false);
          }}
          submitBtnText='Save'
        >
          <p>
            To generate a Tempest Access Token, please visit your Tempest
            account settings page. Once you have generated the token, please
            enter it below.
          </p>
          <input
            type='text'
            value={accessTokenInput}
            onChange={(event) => setAccessTokenInput(event.target.value)}
            style={{ width: '100%' }}
          />
        </Modal>
      )}
    </div>
  );
}

function ClientSettingsTab(props) {
  const statesQuery =
    env.REACT_APP_REGION === 'UK' ? useCountries() : useStates();

  const defaultChecked = {};

  props.docTypes &&
    props.docTypes.length &&
    props.docTypes
      .filter((doc) => {
        if (doc.hasOwnProperty('is_checked')) {
          return doc.is_checked;
        }

        return true;
      })
      .forEach((doc) => {
        defaultChecked[doc.id] = true;
      });

  if (statesQuery.isLoading) return <Loading />;

  const filterTabData = () => {
    let projectTabData = {};

    props.projectTabs &&
      props.projectTabs.forEach((tab) => {
        projectTabData[tab.id] = tab.isChecked;
      });

    return projectTabData;
  };

  const filterPublicViewTabData = () => {
    let data = {};
    props.publicViewTabs &&
      props.publicViewTabs.forEach((tab) => {
        data[tab.id] = tab.isChecked;
      });
    return data;
  };

  const getDocumentDate = () => {
    const data = {
      name: 'Disable Document Created/Updated date',
      id: '1',
      1: !props.document_date,
    };
    const defaultdata = {
      name: 'Disable Document Created/Updated date',
      id: '1',
      1: false,
    };

    return props.document_date === undefined ? defaultdata : data;
  };

  return (
    <FormContextProvider
      initialValue={{
        ...getInitialData(props.client),
        docTypes: defaultChecked,
        projectTabs: filterTabData(),
        publicViewTabs: filterPublicViewTabData(),
        document_date: getDocumentDate(),
      }}
    >
      <TooltipContextProvider>
        <ClientSettingsTabUI
          states={statesQuery.data}
          {...props}
          document_date={getDocumentDate()}
          docTypes={props.docTypes}
          projectTablis={props.projectTabs}
          publicViewTabs={props.publicViewTabs}
          docTypesRefetch={props.docTypesRefetch}
        />
      </TooltipContextProvider>
    </FormContextProvider>
  );
}

ClientSettingsTab.propTypes = {
  client: clientShape,
  handleSubmit: func.isRequired,
  docTypes: arrayOf(shape({ id: number, name: string })),
  projectTabs: arrayOf(shape({ id: number, name: string })),
  newDocTypes: array,
  setNewDocTypes: func,
  toSubmitOrderData: array,
  setToSubmitOrderData: func,
};

export default ClientSettingsTab;
